import { AutoComplete, Button, Select } from "antd";
import React, { useEffect, useState } from "react";
import { Client } from "../../entities/interfaces/client";
import ClientService from "../../services/clients.service";
import ManageClient from "../clients/ManageClient";
import { isPhoneNumber } from "../../utils/helpers";
import { CreateClientDto } from "../../entities/dto/client.dto";
import { X } from "react-feather";
import { CloseCircleFilled } from "@ant-design/icons";

type Props = {
  value: Client | undefined;
  onChange: (client?: Client) => void;
  disabled: boolean;
};

let timeout: ReturnType<typeof setTimeout> | null;
let currentValue: string;

const ClientSelector: React.FC<Props> = ({ onChange, disabled, value }) => {
  const [data, setData] = useState<Client[]>([]);
  const [input, setInput] = useState<string>("");
  const [defaultClientValue, setDefaultClientValue] = useState<
    Partial<CreateClientDto> | undefined
  >(undefined);
  const [showClientCreate, setShowClientCreate] = useState(false);
  const [createClientModal, setCreateClientModal] = useState(false);

  useEffect(() => {
    if (!value) {
      onChange(undefined);
      setInput("");
      setShowClientCreate(false);
      setData([]);
    }
  }, [value]);

  const fetchData = (value: string) => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
    currentValue = value;
    setInput(value);

    const fetcher = async () => {
      const res = await ClientService.list({ search: value }, true);
      if (res.success) {
        if (res.data.length) {
          setData(res.data);
          if (showClientCreate) {
            setShowClientCreate(false);
          }
        } else {
          setData([]);
          setShowClientCreate(true);
        }
      }
    };
    if (value) {
      timeout = setTimeout(fetcher, 500);
    }
  };

  const handleSearch = (newValue: string) => {
    fetchData(newValue);
  };

  const handleChange = (newValue: string) => {
    const selected = data.find((item) => item.id === newValue);
    onChange(selected);
    setInput(selected?.name || "");
  };

  const handleClientChange = (value: any) => {
    setData([value]);
    onChange(value);
  };

  const handleCreateClientBtnClick = () => {
    if (isPhoneNumber(input)) {
      setDefaultClientValue({ phone: input });
    } else {
      setDefaultClientValue({ name: input });
    }
    setCreateClientModal(true);
  };

  const closeCreateClientModal = () => {
    setCreateClientModal(false);
  };

  return (
    <div className=" mb-5 flex gap-3">
      <AutoComplete
        className="flex-1"
        showSearch
        size="large"
        value={input}
        placeholder={"Search Clients"}
        defaultActiveFirstOption={false}
        suffixIcon={null}
        allowClear
        filterOption={false}
        disabled={disabled}
        onSearch={handleSearch}
        onChange={handleChange}
        clearIcon={
          <CloseCircleFilled
            style={{ fontSize: "20px" }}
            className="-m-1 text-primary"
          />
        }
        onBlur={() => {}}
        onClear={() => {
          onChange(undefined);
          setInput("");
          setShowClientCreate(false);
          setData([]);
        }}
        notFoundContent={null}
        options={data.map((item) => ({
          value: item.id,
          label: (
            <p>
              <span className="font-bold mr-2">{item.name}</span>
              {item.phone}
            </p>
          ),
        }))}
      />
      {showClientCreate && (
        <Button
          type="primary"
          size="large"
          onClick={handleCreateClientBtnClick}
        >
          Create client
        </Button>
      )}
      <ManageClient
        open={createClientModal}
        onChange={handleClientChange}
        onClose={closeCreateClientModal}
        defaultValue={defaultClientValue}
      />
    </div>
  );
};

export default ClientSelector;
